import { useState } from "react";

// Extracts the 11-char YouTube video id from watch / embed / youtu.be / nocookie URLs.
export const getYouTubeId = (url: string): string | null => {
  if (!url) return null;
  const m = url.match(
    /(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?v=|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  if (m) return m[1];
  // Bare id (already just the id)
  if (/^[A-Za-z0-9_-]{11}$/.test(url)) return url;
  return null;
};

type YouTubeFacadeProps = {
  url: string;
  title?: string;
  className?: string;
};

// A lightweight "facade" for a YouTube embed: shows the video thumbnail + a
// play button, and only injects the real (cookieless) YouTube <iframe> when the
// user clicks. This means no player JS or third-party cookies load on page view
// — better performance and it clears Lighthouse's cookie/iframe flags — while
// still playing the video in-place on demand.
export default function YouTubeFacade({ url, title, className }: YouTubeFacadeProps) {
  const [activated, setActivated] = useState(false);
  const id = getYouTubeId(url);

  // Unknown URL shape — fall back to a plain cookieless iframe so nothing breaks.
  if (!id) {
    return (
      <iframe
        src={url}
        loading="lazy"
        title={title || "YouTube video"}
        className={className || "absolute top-0 left-0 h-full w-full"}
        allowFullScreen
      />
    );
  }

  const posClasses = "absolute top-0 left-0 h-full w-full";

  if (activated) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1`}
        loading="lazy"
        title={title || "YouTube video"}
        className={className || posClasses}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      aria-label={title ? `Play video: ${title}` : "Play video"}
      className={`group ${className || posClasses} block cursor-pointer overflow-hidden border-0 p-0`}
      style={{
        backgroundImage: `url(https://i.ytimg.com/vi_webp/${id}/hqdefault.webp), url(https://i.ytimg.com/vi/${id}/hqdefault.jpg)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Dark hover veil */}
      <span className="absolute inset-0 bg-black/10 transition-colors group-hover:bg-black/25" />
      {/* Play button: a hard-edged stage plate that turns signal blue on hover */}
      <span className="absolute left-1/2 top-1/2 flex h-[48px] w-[68px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[3px] bg-stage-deep/85 transition-colors group-hover:bg-signal group-focus-visible:bg-signal">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="#fff" aria-hidden="true">
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
    </button>
  );
}
